import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAuthData } from "./authSlice";
import { updatePriceRange } from "./filtersSlice";

const cache = new Map();
const CACHE_EXPIRATION_TIME = process.env.REACT_APP_CACHE_TIME || 30000;
const USE_CACHE = process.env.REACT_APP_CACHE === "true";

export const fetchProducts = createAsyncThunk(
  "products/fetchProducts",
  async (pagination, { getState, dispatch }) => {
    try {
      const state = getState();
      const { currentPage, itemsPerPage, order, lastSearch } = state.products;

      const authData = getAuthData();
      if (
        !authData?.token ||
        !authData?.groupCode ||
        !authData?.companyCode ||
        !authData?.aux
      ) {
        throw new Error("signin");
      }

      const {
        radioFilters = {},
        checkboxFilters = {},
        searchParam,
        single: singleSelect = {},
        priceRange = { range: [] },
      } = pagination;

      // 🔥 RESET PAGE WHEN SEARCH CHANGES
      if (searchParam !== lastSearch && currentPage !== 0) {
        dispatch(setCurrentPage(0));
        return null; // stop execution — component will refetch with page=0
      }

      // Process checkbox filters
      const checkboxParams = Object.entries(checkboxFilters)
        .filter(([_, values]) => values?.length > 0)
        .map(([filterId, values]) => [
          filterId,
          "in",
          values.map((item) => item?.value),
        ]);

      const filterParams = Object.entries(radioFilters).map(([id, value]) => [
        id,
        "=",
        value,
      ]);

      const singleSelectParams = Object.entries(singleSelect).map(
        ([id, value]) => [id, "=", value],
      );

      const priceRangeParams = [
        priceRange?.range?.[0] != null && [
          "min_price",
          "=",
          priceRange.range[0],
        ],
        priceRange?.range?.[1] != null && [
          "max_price",
          "=",
          priceRange.range[1],
        ],
      ].filter(Boolean);

      // Build request
      const requestBody = {
        login: [
          ["token", "=", authData.token],
          ["group_code", "=", authData.groupCode],
          ["company_code", "=", authData.companyCode],
          ["user_id", "=", authData.user_id],
        ],
        params: [
          ...checkboxParams,
          ...filterParams,
          ...singleSelectParams,
          ["name", "like", searchParam],
          ["aux", "=", authData.aux],
          ...priceRangeParams,
        ],
        page: [
          ["page_size", "=", itemsPerPage],
          ["page_number", "=", currentPage + 1],
        ],
        ...(order &&
          order !== "latest" && {
            order: ["discounted_selling_price", "=", order],
          }),
      };

      // Build cache key
      const cacheKey = JSON.stringify({
        params: requestBody.params,
        page: requestBody.page,
        order: requestBody.order,
      });

      // Cache
      if (USE_CACHE) {
        const cached = cache.get(cacheKey);
        if (cached) {
          const { data, timestamp } = cached;
          if (Date.now() - timestamp < CACHE_EXPIRATION_TIME) {
            if (data?.max_price != null) {
              dispatch(updatePriceRange({ max_price: data.max_price }));
            }
            return data;
          }
          cache.delete(cacheKey);
        }
      }

      // Fetch
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}clientPortal/Catalogue`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      const data = await response.json();

      if (USE_CACHE) {
        cache.set(cacheKey, { data, timestamp: Date.now() });
      }

      if (data?.max_price != null) {
        dispatch(updatePriceRange({ max_price: data.max_price }));
      }

      return data;
    } catch (error) {
      throw new Error("Could not fetch products");
    }
  },
);

const productSlice = createSlice({
  name: "products",
  initialState: {
    products: [],
    status: "idle",
    error: null,
    currentPage: 0,
    itemsPerPage: 12,
    order: null,
    lastSearch: "",
  },
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.itemsPerPage = action.payload;
    },
    setOrder: (state, action) => {
      state.order = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        if (action.payload === null) return;

        state.status = "succeeded";
        state.products = action.payload;

        const searchParam = action.meta?.arg?.searchParam;
        if (searchParam !== undefined) {
          state.lastSearch = searchParam;
        }
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  },
});

export const { setCurrentPage, setItemsPerPage, setOrder } =
  productSlice.actions;

export default productSlice.reducer;
